import { create } from "zustand";
import type { Area, Blueprint, ClusterChildSpec, CustomRoom, Item, Layers, Measurement, Scene, SnapSettings, SvgData, Team, Tool, Unit, Wall, WallMaterial } from "./types";
import { CATALOG_BY_ID } from "./catalog";
import { explodeWorkstation } from "./explode";
import { findMergeCandidate, canMerge, mergeIntoWorkstation } from "./merge";

const MAX_HISTORY = 50;

const uid = () => Math.random().toString(36).slice(2, 10);

const EMPTY_SCENE: Scene = { items: [], walls: [], measurements: [], areas: [], blueprint: null };

// Pluggable persister — wired by the cloud-sync layer after login.
let _persister: ((scene: Scene) => void) | null = null;
export function setScenePersister(fn: ((scene: Scene) => void) | null) {
  _persister = fn;
}
function saveScene(scene: Scene) {
  if (_persister) _persister(scene);
}

interface State {
  scene: Scene;
  tool: Tool;
  unit: Unit;
  gridSize: number; // cm
  snap: boolean;
  snapSettings: SnapSettings;
  layers: Layers;
  selectedIds: string[];
  zoom: number;
  pan: { x: number; y: number };
  blueprintSelected: boolean;
  wallCandidates: Omit<Wall, "id">[] | null;
  // wall drawing
  /** Material + thickness applied to newly-drawn walls. */
  wallDraft: { material: WallMaterial; thickness: number };
  pendingPoint: { x: number; y: number } | null;
  pendingAreaPoints: { x: number; y: number }[];
  history: Scene[];
  future: Scene[];

  setTool: (t: Tool) => void;
  setUnit: (u: Unit) => void;
  setZoom: (z: number) => void;
  setPan: (p: { x: number; y: number }) => void;
  toggleSnap: () => void;
  setGridSize: (g: number) => void;
  setSnapSettings: (patch: Partial<SnapSettings>) => void;
  setLayers: (patch: Partial<Layers>) => void;

  addFromCatalog: (catalogId: string, x: number, y: number, overrides?: { w?: number; h?: number; groupId?: string }) => void;
  /** Drop a saved custom-room preset onto the canvas as a fresh cluster. */
  addCustomRoom: (customRoomId: string, x: number, y: number) => void;
  /** Snapshot the current selection (or single grouped room) as a reusable
   *  room preset stored on the scene under `customRooms`. */
  saveSelectionAsRoom: (name: string, color?: string) => CustomRoom | null;
  removeCustomRoom: (customRoomId: string) => void;
  renameCustomRoom: (customRoomId: string, name: string) => void;
  /** Hide a built-in catalog component from the Library (persisted on the scene). */
  hideCatalogItem: (catalogId: string) => void;
  /** Un-hide every hidden catalog component. */
  restoreHiddenCatalog: () => void;
  /** Capture the current child layout of a clustered room as the new preset
   *  for that catalog id (stored in scene.customClusters, persisted with the scene). */
  saveClusterPreset: (roomId: string) => { count: number; type: string } | null;
  /** Drop the local custom preset for a catalog id (revert to catalog default). */
  clearClusterPreset: (catalogId: string) => void;
  addCustomSvg: (x: number, y: number, asset: { name: string; inner: string; viewBox: string; width: number; height: number; palette: string[] }) => void;
  updateItem: (id: string, patch: Partial<Item>, options?: { persist?: boolean }) => void;
  updateItems: (patches: { id: string; patch: Partial<Item> }[], options?: { persist?: boolean }) => void;
  persistScene: () => void;
  removeSelected: () => void;
  removeItem: (id: string) => void;
  duplicateSelected: () => void;
  rotateSelected: (delta: number) => void;
  select: (ids: string[]) => void;
  toggleSelect: (id: string, additive?: boolean) => void;
  clearSelection: () => void;

  explodeItem: (id: string) => void;
  mergeSelection: () => void;
  ungroupSelected: () => void;
  selectGroup: (groupId: string) => void;
  groupSelection: () => void;

  startWallPoint: (x: number, y: number) => void;
  cancelPending: () => void;
  commitWall: (x: number, y: number) => void;
  setWallDraft: (patch: Partial<{ material: WallMaterial; thickness: number }>) => void;
  commitMeasurement: (x: number, y: number) => void;
  addAreaPoint: (x: number, y: number) => void;
  finishArea: () => void;
  removeArea: (id: string) => void;
  removeWall: (id: string) => void;
  updateWall: (id: string, patch: Partial<Wall>) => void;
  removeMeasurement: (id: string) => void;
  addWallsBulk: (walls: Omit<Wall, "id">[]) => void;
  setWallCandidates: (walls: Omit<Wall, "id">[] | null) => void;
  acceptWallCandidates: () => void;

  setBlueprint: (b: Blueprint | null) => void;
  updateBlueprint: (patch: Partial<Blueprint>) => void;
  selectBlueprint: (v: boolean) => void;

  pushHistory: () => void;
  undo: () => void;
  redo: () => void;
  clearAll: () => void;
  loadScene: (s: Scene) => void;

  // Allocation — teams / departments
  addTeam: (name: string, color: string) => Team;
  updateTeam: (id: string, patch: Partial<Team>) => void;
  removeTeam: (id: string) => void;
}

export const usePlanner = create<State>((set, get) => ({
  scene: { ...EMPTY_SCENE },
  tool: "select",
  unit: "cm",
  // Sub-cell = 4 in (~10.16 cm). Major cell = 6 sub-cells = 2 ft x 2 ft.
  gridSize: 10.16,
  snap: true,
  snapSettings: { grid: true, angle: false, vertex: true },
  layers: { blueprint: true, rooms: true, walls: true, items: true, measurements: true, areas: true, grid: true },
  selectedIds: [],
  zoom: 1,
  pan: { x: 200, y: 150 },
  pendingPoint: null,
  wallDraft: { material: "cemented", thickness: 10 },
  pendingAreaPoints: [],
  history: [],
  future: [],
  blueprintSelected: false,
  wallCandidates: null,

  setTool: (t) => set({ tool: t, pendingPoint: null, pendingAreaPoints: [], selectedIds: t === "select" ? get().selectedIds : [], blueprintSelected: t === "select" ? get().blueprintSelected : false }),
  setUnit: (u) => set({ unit: u }),
  setZoom: (z) => set({ zoom: Math.max(0.2, Math.min(4, z)) }),
  setPan: (p) => set({ pan: p }),
  toggleSnap: () => set((s) => ({ snap: !s.snap, snapSettings: { ...s.snapSettings, grid: !s.snap } })),
  setGridSize: (g) => set({ gridSize: g }),
  setSnapSettings: (patch) => set((s) => ({ snapSettings: { ...s.snapSettings, ...patch }, snap: patch.grid ?? s.snap })),
  setLayers: (patch) => set((s) => ({ layers: { ...s.layers, ...patch } })),

  pushHistory: () => {
    const { scene, history } = get();
    const next = [...history, JSON.parse(JSON.stringify(scene))].slice(-MAX_HISTORY);
    set({ history: next, future: [] });
  },

  addFromCatalog: (catalogId, x, y, overrides) => {
    const c = CATALOG_BY_ID[catalogId];
    if (!c) return;
    get().pushHistory();
    const w = overrides?.w ?? c.w;
    const h = overrides?.h ?? c.h;
    const targetGroupId = overrides?.groupId;
    const item: Item = {
      id: uid(),
      type: c.id,
      kind: c.kind,
      shape: c.shape,
      label: c.label,
      x: x - w / 2,
      y: y - h / 2,
      w,
      h,
      rotation: 0,
      color: c.color,
      seats: c.seats,
    };
    // Dropping into an existing cluster — attach and skip spawning a nested cluster.
    if (targetGroupId) {
      item.groupId = targetGroupId;
      const scene = { ...get().scene, items: [...get().scene.items, item] };
      set({ scene, selectedIds: [item.id] });
      saveScene(scene);
      return;
    }
    const extras: Item[] = [];
    // Spawn default furniture cluster (e.g. Director Cabin preset). Custom
    // per-project preset overrides the catalog default when present.
    const customCluster = get().scene.customClusters?.[c.id];
    const cluster: ClusterChildSpec[] | undefined = customCluster?.length
      ? customCluster
      : (c.defaultCluster as ClusterChildSpec[] | undefined);
    if (cluster && cluster.length) {
      const sx = w / c.w;
      const sy = h / c.h;
      const groupId = uid();
      item.groupId = groupId;
      for (const child of cluster) {
        extras.push({
          id: uid(),
          type: child.type,
          kind: child.kind,
          shape: child.shape,
          label: child.label,
          x: item.x + child.x * sx,
          y: item.y + child.y * sy,
          w: child.w * sx,
          h: child.h * sy,
          rotation: child.rotation ?? 0,
          color: child.color,
          seats: child.seats,
          groupId,
          flipArc: child.flipArc,
        });
      }
    }
    const scene = { ...get().scene, items: [...get().scene.items, item, ...extras] };
    set({ scene, selectedIds: [item.id] });
    saveScene(scene);
  },

  addCustomRoom: (customRoomId, x, y) => {
    const room = (get().scene.customRooms ?? []).find((r) => r.id === customRoomId);
    if (!room) return;
    get().pushHistory();
    const groupId = uid();
    const extras: Item[] = [];
    // Spawn a transparent room rectangle as the cluster's anchor so the
    // whole group can be moved/duplicated like a catalog room.
    const anchor: Item = {
      id: uid(),
      type: customRoomId,
      kind: "room",
      shape: "rect",
      label: room.name,
      x: x - room.w / 2,
      y: y - room.h / 2,
      w: room.w,
      h: room.h,
      rotation: 0,
      color: room.color ?? "oklch(0.92 0.05 250 / 0.25)",
      groupId,
    };
    for (const child of room.children) {
      extras.push({
        id: uid(),
        type: child.type,
        kind: child.kind,
        shape: child.shape,
        label: child.label,
        x: anchor.x + child.x,
        y: anchor.y + child.y,
        w: child.w,
        h: child.h,
        rotation: child.rotation ?? 0,
        color: child.color,
        seats: child.seats,
        groupId,
        flipArc: child.flipArc,
      });
    }
    const scene = { ...get().scene, items: [...get().scene.items, anchor, ...extras] };
    set({ scene, selectedIds: [anchor.id] });
    saveScene(scene);
  },

  saveSelectionAsRoom: (name, color) => {
    const { scene, selectedIds } = get();
    const sel = scene.items.filter((i) => selectedIds.includes(i.id));
    if (sel.length < 1) return null;
    // Compute bounding box (ignoring rotation for now — matches existing
    // cluster math elsewhere).
    const minX = Math.min(...sel.map((i) => i.x));
    const minY = Math.min(...sel.map((i) => i.y));
    const maxX = Math.max(...sel.map((i) => i.x + i.w));
    const maxY = Math.max(...sel.map((i) => i.y + i.h));
    const w = Math.max(10, maxX - minX);
    const h = Math.max(10, maxY - minY);
    // Skip the bounding "room" item itself if present — we re-create our own anchor.
    const children: ClusterChildSpec[] = sel
      .filter((i) => !(i.kind === "room" && Math.abs(i.x - minX) < 1 && Math.abs(i.y - minY) < 1 && Math.abs(i.w - w) < 1 && Math.abs(i.h - h) < 1))
      .map((i) => ({
        type: i.type,
        label: i.label,
        kind: i.kind,
        shape: i.shape,
        x: i.x - minX,
        y: i.y - minY,
        w: i.w,
        h: i.h,
        rotation: i.rotation,
        color: i.color,
        seats: i.seats,
        flipArc: i.flipArc,
      }));
    if (!children.length) return null;
    const preset: CustomRoom = {
      id: `myroom-${uid()}`,
      name: name.trim() || "Custom Room",
      w,
      h,
      color,
      children,
    };
    get().pushHistory();
    const list = [...(scene.customRooms ?? []), preset];
    const next = { ...scene, customRooms: list };
    set({ scene: next });
    saveScene(next);
    return preset;
  },

  removeCustomRoom: (customRoomId) => {
    const list = (get().scene.customRooms ?? []).filter((r) => r.id !== customRoomId);
    const next = { ...get().scene, customRooms: list.length ? list : undefined };
    set({ scene: next });
    saveScene(next);
  },

  hideCatalogItem: (catalogId) => {
    const cur = get().scene.hiddenCatalogIds ?? [];
    if (cur.includes(catalogId)) return;
    const next = { ...get().scene, hiddenCatalogIds: [...cur, catalogId] };
    set({ scene: next });
    saveScene(next);
  },

  restoreHiddenCatalog: () => {
    const next = { ...get().scene, hiddenCatalogIds: undefined };
    set({ scene: next });
    saveScene(next);
  },

  renameCustomRoom: (customRoomId, name) => {
    const list = (get().scene.customRooms ?? []).map((r) => (r.id === customRoomId ? { ...r, name } : r));
    const next = { ...get().scene, customRooms: list };
    set({ scene: next });
    saveScene(next);
  },

  saveClusterPreset: (roomId) => {
    const room = get().scene.items.find((i) => i.id === roomId);
    if (!room || room.kind !== "room" || !room.groupId) return null;
    const c = CATALOG_BY_ID[room.type];
    if (!c) return null;
    // Normalize children to the catalog's natural size so future drops scale correctly.
    const sx = c.w / Math.max(1, room.w);
    const sy = c.h / Math.max(1, room.h);
    const children = get().scene.items.filter(
      (i) => i.groupId === room.groupId && i.id !== room.id,
    );
    const spec: ClusterChildSpec[] = children.map((ch) => ({
      type: ch.type,
      label: ch.label,
      kind: ch.kind,
      shape: ch.shape,
      x: (ch.x - room.x) * sx,
      y: (ch.y - room.y) * sy,
      w: ch.w * sx,
      h: ch.h * sy,
      rotation: ch.rotation,
      color: ch.color,
      seats: ch.seats,
      flipArc: ch.flipArc,
    }));
    const next: Scene = {
      ...get().scene,
      customClusters: { ...(get().scene.customClusters ?? {}), [room.type]: spec },
    };
    set({ scene: next });
    saveScene(next);
    return { count: spec.length, type: room.type };
  },

  clearClusterPreset: (catalogId) => {
    const cur = get().scene.customClusters;
    if (!cur || !cur[catalogId]) return;
    const { [catalogId]: _, ...rest } = cur;
    const next: Scene = { ...get().scene, customClusters: Object.keys(rest).length ? rest : undefined };
    set({ scene: next });
    saveScene(next);
  },

  addCustomSvg: (x, y, asset) => {
    get().pushHistory();
    const svg: SvgData = { inner: asset.inner, viewBox: asset.viewBox, palette: asset.palette, overrides: {} };
    const w = asset.width;
    const h = asset.height;
    const item: Item = {
      id: uid(),
      type: "custom-svg",
      kind: "furniture",
      shape: "svg",
      label: asset.name,
      x: x - w / 2,
      y: y - h / 2,
      w,
      h,
      rotation: 0,
      svg,
    };
    const scene = { ...get().scene, items: [...get().scene.items, item] };
    set({ scene, selectedIds: [item.id] });
    saveScene(scene);
  },

  updateItem: (id, patch, options) => {
    const items = get().scene.items;
    const target = items.find((it) => it.id === id);
    // When resizing/moving a room that owns a cluster, translate & scale its
    // children proportionally so the cluster keeps its relative layout.
    let childPatches: Map<string, Partial<Item>> | null = null;
    if (target && target.kind === "room" && target.groupId) {
      const nx = patch.x ?? target.x;
      const ny = patch.y ?? target.y;
      const nw = patch.w ?? target.w;
      const nh = patch.h ?? target.h;
      const dx = nx - target.x;
      const dy = ny - target.y;
      const sx = nw / Math.max(1, target.w);
      const sy = nh / Math.max(1, target.h);
      if (dx !== 0 || dy !== 0 || sx !== 1 || sy !== 1) {
        childPatches = new Map();
        for (const ch of items) {
          if (ch.id === target.id || ch.groupId !== target.groupId) continue;
          const relX = ch.x - target.x;
          const relY = ch.y - target.y;
          childPatches.set(ch.id, {
            x: nx + relX * sx,
            y: ny + relY * sy,
            w: ch.w * sx,
            h: ch.h * sy,
          });
        }
      }
    }
    const scene = {
      ...get().scene,
      items: items.map((it) => {
        if (it.id === id) return { ...it, ...patch };
        const cp = childPatches?.get(it.id);
        return cp ? { ...it, ...cp } : it;
      }),
    };
    set({ scene });
    if (options?.persist !== false) saveScene(scene);
  },

  updateItems: (patches, options) => {
    if (!patches.length) return;
    const byId = new Map(patches.map((p) => [p.id, p.patch]));
    const scene = {
      ...get().scene,
      items: get().scene.items.map((it) => {
        const patch = byId.get(it.id);
        return patch ? { ...it, ...patch } : it;
      }),
    };
    set({ scene });
    if (options?.persist !== false) saveScene(scene);
  },

  persistScene: () => {
    saveScene(get().scene);
  },

  removeItem: (id) => {
    get().pushHistory();
    const items = get().scene.items;
    const target = items.find((it) => it.id === id);
    const groupIds = new Set<string>();
    const ids = new Set<string>([id]);
    if (target?.groupId) groupIds.add(target.groupId);
    if (groupIds.size) {
      items.forEach((it) => {
        if (it.groupId && groupIds.has(it.groupId)) ids.add(it.id);
      });
    }
    const scene = { ...get().scene, items: items.filter((it) => !ids.has(it.id)) };
    set({ scene, selectedIds: get().selectedIds.filter((s) => !ids.has(s)) });
    saveScene(scene);
  },

  removeSelected: () => {
    const { selectedIds, scene } = get();
    if (!selectedIds.length) return;
    get().pushHistory();
    const groupIds = new Set<string>();
    scene.items.forEach((it) => {
      if (selectedIds.includes(it.id) && it.groupId) groupIds.add(it.groupId);
    });
    const itemIdsToRemove = new Set<string>(selectedIds);
    if (groupIds.size) {
      scene.items.forEach((it) => {
        if (it.groupId && groupIds.has(it.groupId)) itemIdsToRemove.add(it.id);
      });
    }
    const next = {
      ...scene,
      items: scene.items.filter((it) => !itemIdsToRemove.has(it.id)),
      walls: scene.walls.filter((w) => !selectedIds.includes(w.id)),
      measurements: scene.measurements.filter((m) => !selectedIds.includes(m.id)),
    };
    set({ scene: next, selectedIds: [] });
    saveScene(next);
  },

  duplicateSelected: () => {
    const { selectedIds, scene } = get();
    if (!selectedIds.length) return;
    get().pushHistory();
    const dupes: Item[] = [];
    // Remap groupIds so duplicated clusters become independent groups,
    // otherwise dragging the duplicate also drags the original.
    const groupMap = new Map<string, string>();
    const selectedSet = new Set(selectedIds);
    // Include all members of any selected group so the duplicate is a complete cluster.
    const groupIds = new Set<string>();
    scene.items.forEach((it) => {
      if (selectedSet.has(it.id) && it.groupId) groupIds.add(it.groupId);
    });
    const toDup = scene.items.filter(
      (it) => selectedSet.has(it.id) || (it.groupId && groupIds.has(it.groupId)),
    );
    toDup.forEach((it) => {
      let newGroupId = it.groupId;
      if (it.groupId) {
        if (!groupMap.has(it.groupId)) groupMap.set(it.groupId, uid());
        newGroupId = groupMap.get(it.groupId);
      }
      dupes.push({ ...it, id: uid(), x: it.x + 30, y: it.y + 30, groupId: newGroupId });
    });
    const next = { ...scene, items: [...scene.items, ...dupes] };
    set({ scene: next, selectedIds: dupes.map((d) => d.id) });
    saveScene(next);
  },

  rotateSelected: (delta) => {
    const { selectedIds, scene } = get();
    if (!selectedIds.length) return;
    get().pushHistory();
    const next = {
      ...scene,
      items: scene.items.map((it) =>
        selectedIds.includes(it.id) ? { ...it, rotation: (it.rotation + delta) % 360 } : it,
      ),
    };
    set({ scene: next });
    saveScene(next);
  },

  select: (ids) => set({ selectedIds: ids }),
  toggleSelect: (id, additive) =>
    set((s) => {
      if (!additive) return { selectedIds: [id] };
      return s.selectedIds.includes(id)
        ? { selectedIds: s.selectedIds.filter((x) => x !== id) }
        : { selectedIds: [...s.selectedIds, id] };
    }),
  clearSelection: () => set({ selectedIds: [], blueprintSelected: false }),

  explodeItem: (id) => {
    const it = get().scene.items.find((i) => i.id === id);
    if (!it) return;
    const parts = explodeWorkstation(it);
    if (parts.length <= 1) return; // not a workstation
    get().pushHistory();
    const items = get().scene.items.filter((x) => x.id !== id).concat(parts);
    const scene = { ...get().scene, items };
    set({ scene, selectedIds: parts.map((p) => p.id) });
    saveScene(scene);
  },

  mergeSelection: () => {
    const { scene, selectedIds } = get();
    const sel = scene.items.filter((i) => selectedIds.includes(i.id));
    const cand = findMergeCandidate(sel, scene.items);
    if (!canMerge(cand) || !cand) return;
    get().pushHistory();
    const memberIds = new Set(cand.members.map((m) => m.id));
    const merged = mergeIntoWorkstation(cand);
    const items = scene.items.filter((i) => !memberIds.has(i.id)).concat(merged);
    const next = { ...scene, items };
    set({ scene: next, selectedIds: [merged.id] });
    saveScene(next);
  },

  ungroupSelected: () => {
    const { scene, selectedIds } = get();
    const sel = scene.items.filter((i) => selectedIds.includes(i.id));
    const gids = new Set(sel.map((i) => i.groupId).filter(Boolean) as string[]);
    if (!gids.size) return;
    get().pushHistory();
    const items = scene.items.map((i) =>
      i.groupId && gids.has(i.groupId) ? { ...i, groupId: undefined } : i,
    );
    const next = { ...scene, items };
    set({ scene: next });
    saveScene(next);
  },

  selectGroup: (groupId) => {
    const ids = get().scene.items.filter((i) => i.groupId === groupId).map((i) => i.id);
    set({ selectedIds: ids });
  },

  groupSelection: () => {
    const { scene, selectedIds } = get();
    if (selectedIds.length < 2) return;
    get().pushHistory();
    const gid = uid();
    const selSet = new Set(selectedIds);
    // Collect any groups that any selected item belongs to, so other
    // members of those groups don't get orphaned in a partial cluster.
    const touchedGroups = new Set<string>();
    scene.items.forEach((i) => {
      if (selSet.has(i.id) && i.groupId) touchedGroups.add(i.groupId);
    });
    const items = scene.items.map((i) => {
      if (selSet.has(i.id)) return { ...i, groupId: gid };
      if (i.groupId && touchedGroups.has(i.groupId)) return { ...i, groupId: undefined };
      return i;
    });
    const next = { ...scene, items };
    set({ scene: next });
    saveScene(next);
  },

  startWallPoint: (x, y) => set({ pendingPoint: { x, y } }),
  cancelPending: () => set({ pendingPoint: null, pendingAreaPoints: [] }),
  commitWall: (x, y) => {
    const start = get().pendingPoint;
    if (!start) return;
    get().pushHistory();
    const { material, thickness } = get().wallDraft;
    const wall: Wall = { id: uid(), x1: start.x, y1: start.y, x2: x, y2: y, thickness, material };
    const next = { ...get().scene, walls: [...get().scene.walls, wall] };
    // Single-segment commit: stop chaining so each wall is intentional.
    set({ scene: next, pendingPoint: null });
    saveScene(next);
  },
  setWallDraft: (patch) => set({ wallDraft: { ...get().wallDraft, ...patch } }),
  commitMeasurement: (x, y) => {
    const start = get().pendingPoint;
    if (!start) {
      set({ pendingPoint: { x, y } });
      return;
    }
    get().pushHistory();
    const m: Measurement = { id: uid(), x1: start.x, y1: start.y, x2: x, y2: y };
    const next = { ...get().scene, measurements: [...get().scene.measurements, m] };
    set({ scene: next, pendingPoint: null });
    saveScene(next);
  },
  removeWall: (id) => {
    get().pushHistory();
    const next = { ...get().scene, walls: get().scene.walls.filter((w) => w.id !== id) };
    set({ scene: next });
    saveScene(next);
  },
  updateWall: (id, patch) => {
    const next = { ...get().scene, walls: get().scene.walls.map((w) => (w.id === id ? { ...w, ...patch } : w)) };
    set({ scene: next });
    saveScene(next);
  },
  removeMeasurement: (id) => {
    get().pushHistory();
    const next = { ...get().scene, measurements: get().scene.measurements.filter((m) => m.id !== id) };
    set({ scene: next });
    saveScene(next);
  },

  addAreaPoint: (x, y) => {
    set({ pendingAreaPoints: [...get().pendingAreaPoints, { x, y }] });
  },
  finishArea: () => {
    const pts = get().pendingAreaPoints;
    if (pts.length < 3) {
      set({ pendingAreaPoints: [] });
      return;
    }
    get().pushHistory();
    const area: Area = { id: uid(), points: pts };
    const next = { ...get().scene, areas: [...get().scene.areas, area] };
    set({ scene: next, pendingAreaPoints: [] });
    saveScene(next);
  },
  removeArea: (id) => {
    get().pushHistory();
    const next = { ...get().scene, areas: get().scene.areas.filter((a) => a.id !== id) };
    set({ scene: next });
    saveScene(next);
  },

  addWallsBulk: (walls) => {
    if (!walls.length) return;
    get().pushHistory();
    const newWalls: Wall[] = walls.map((w) => ({ ...w, id: uid() }));
    const next = { ...get().scene, walls: [...get().scene.walls, ...newWalls] };
    set({ scene: next });
    saveScene(next);
  },
  setWallCandidates: (walls) => set({ wallCandidates: walls }),
  acceptWallCandidates: () => {
    const c = get().wallCandidates;
    if (!c || !c.length) {
      set({ wallCandidates: null });
      return;
    }
    get().pushHistory();
    const newWalls: Wall[] = c.map((w) => ({ ...w, id: uid() }));
    const next = { ...get().scene, walls: [...get().scene.walls, ...newWalls] };
    set({ scene: next, wallCandidates: null });
    saveScene(next);
  },

  setBlueprint: (b) => {
    get().pushHistory();
    const next = { ...get().scene, blueprint: b };
    set({ scene: next, blueprintSelected: !!b });
    saveScene(next);
  },
  updateBlueprint: (patch) => {
    const bp = get().scene.blueprint;
    if (!bp) return;
    const next = { ...get().scene, blueprint: { ...bp, ...patch } };
    set({ scene: next });
    saveScene(next);
  },
  selectBlueprint: (v) => set({ blueprintSelected: v, selectedIds: v ? [] : get().selectedIds }),

  undo: () => {
    const { history, scene, future } = get();
    if (!history.length) return;
    const prev = history[history.length - 1];
    set({
      scene: prev,
      history: history.slice(0, -1),
      future: [...future, scene].slice(-MAX_HISTORY),
      selectedIds: [],
      pendingPoint: null,
    });
    saveScene(prev);
  },
  redo: () => {
    const { future, scene, history } = get();
    if (!future.length) return;
    const next = future[future.length - 1];
    set({
      scene: next,
      future: future.slice(0, -1),
      history: [...history, scene].slice(-MAX_HISTORY),
      selectedIds: [],
    });
    saveScene(next);
  },
  clearAll: () => {
    get().pushHistory();
    const next: Scene = { items: [], walls: [], measurements: [], areas: [], blueprint: null };
    set({ scene: next, selectedIds: [], blueprintSelected: false });
    saveScene(next);
  },
  loadScene: (s) => {
    get().pushHistory();
    set({ scene: s, selectedIds: [] });
    saveScene(s);
  },

  addTeam: (name, color) => {
    const team: Team = { id: `team-${uid()}`, name: name.trim() || "Team", color };
    const teams = [...(get().scene.teams ?? []), team];
    const next: Scene = { ...get().scene, teams };
    set({ scene: next });
    saveScene(next);
    return team;
  },
  updateTeam: (id, patch) => {
    const teams = (get().scene.teams ?? []).map((t) => (t.id === id ? { ...t, ...patch } : t));
    const next: Scene = { ...get().scene, teams };
    set({ scene: next });
    saveScene(next);
  },
  removeTeam: (id) => {
    const teams = (get().scene.teams ?? []).filter((t) => t.id !== id);
    // Clear teamId from any items that referenced it.
    const items = get().scene.items.map((i) => (i.teamId === id ? { ...i, teamId: undefined } : i));
    const next: Scene = { ...get().scene, teams: teams.length ? teams : undefined, items };
    set({ scene: next });
    saveScene(next);
  },
}));

export function formatDistance(cm: number, unit: Unit): string {
  if (unit === "cm") return `${Math.round(cm)} cm`;
  if (unit === "m") return `${(cm / 100).toFixed(2)} m`;
  return `${(cm / 30.48).toFixed(2)} ft`;
}

export function formatArea(cm2: number, unit: Unit): string {
  if (unit === "ft") {
    const ft2 = cm2 / (30.48 * 30.48);
    return `${ft2.toFixed(1)} ft²`;
  }
  const m2 = cm2 / 10000;
  if (m2 >= 0.1 || unit === "m") return `${m2.toFixed(2)} m²`;
  return `${Math.round(cm2)} cm²`;
}